import MuiAutocomplete from '@mui/material/Autocomplete';
import { TextInput } from '@src/common/components/TextInput';
import { css } from '@src/common/styles/Styles';
import clsx from 'clsx';
import { useId } from 'react';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export type FormAutocompleteOptions = readonly string[];

interface Props<T extends FieldValues> {
  fieldName: Path<T>;
  options: FormAutocompleteOptions;
  control: Control<T>;
  label?: string;
  disabled?: boolean;
  ariaLabel?: string;
  onChange?: (value?: string) => void;
  tabIndex?: number;
}

const handleAutocompleteChange = (
  data: unknown,
  fieldOnChange: (value: string) => void,
  externalOnChange?: (value?: string) => void,
) => {
  const newValue = typeof data === 'string' ? data : undefined;
  fieldOnChange(newValue ?? '');
  externalOnChange?.(newValue);
};

const handleInputChange = (
  newInputValue: string,
  fieldOnChange: (value: string) => void,
  externalOnChange?: (value?: string) => void,
) => {
  const value = newInputValue || undefined;
  fieldOnChange(newInputValue || '');
  externalOnChange?.(value);
};

export const FormAutocomplete = <T extends FieldValues>({
  fieldName,
  options,
  control,
  label,
  disabled,
  ariaLabel,
  onChange,
  tabIndex,
}: Props<T>) => {
  const fieldId = useId();
  const { t } = useTranslation();

  return (
    <Controller
      control={control}
      name={fieldName}
      render={({
        field: { disabled: _disabled, ...field },
        fieldState: { error },
        formState: { disabled: formDisabled },
      }) => {
        const isDisabled = disabled || formDisabled;
        const fieldLabel = label;
        const errorMessage = error && t(`formErrors.${error.message}`);

        return (
          <MuiAutocomplete
            freeSolo
            {...field}
            readOnly={isDisabled}
            disabled={isDisabled}
            classes={{ root: clsx({ 'Mui-readOnly': isDisabled }) }}
            options={options}
            className={fullWidth}
            value={field.value ?? ''}
            onChange={(_, data) =>
              handleAutocompleteChange(data, field.onChange, onChange)
            }
            onInputChange={(_, newInputValue) =>
              handleInputChange(newInputValue, field.onChange, onChange)
            }
            id={fieldId}
            renderInput={(params) => (
              <TextInput
                {...params}
                readOnly={isDisabled}
                label={fieldLabel}
                error={errorMessage}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    readOnly: isDisabled,
                  },
                  htmlInput: {
                    ...params.inputProps,
                    'aria-label': ariaLabel,
                    tabIndex,
                  },
                }}
              />
            )}
          />
        );
      }}
    />
  );
};

const fullWidth = css({
  flex: 1,
});
